class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.localSearchIndex = null;
    this.localSearchIndexPromise = null;
    this.nativeSearchTimer = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    // Yaax Vida: wait until the second character before suggesting products.
    // This reduces noise while still surfacing useful matches very early.
    if (this.searchTerm.length < 2) {
      this.renderMinimumHint();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    this.ensureLocalSearchIndex();
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();

    // Local typo-tolerant results are loaded only after the visitor interacts with search.
    this.renderLocalPreviewAsync(searchTerm);
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    clearTimeout(this.nativeSearchTimer);
    this.nativeSearchTimer = setTimeout(() => {
      this.abortController.abort();
      this.abortController = new AbortController();
      fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
        { signal: this.abortController.signal }
      )
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          if (this.getQuery() !== searchTerm) return;
          const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
          if (!section) return;
          const resultsMarkup = section.innerHTML;
          this.allPredictiveSearchInstances.forEach((instance) => {
            instance.cachedResults[queryKey] = resultsMarkup;
          });
          this.renderSearchResults(resultsMarkup, searchTerm);
        })
        .catch((error) => {
          if (error?.name === 'AbortError' || error?.code === 20) return;
          if (!this.querySelector('.yaax-local-predictive')) this.close();
          console.warn('Predictive search unavailable; local suggestions remain active.', error);
        });
    }, 120);
  }

  ensureLocalSearchIndex() {
    if (Array.isArray(this.localSearchIndex)) return Promise.resolve(this.localSearchIndex);
    if (Array.isArray(window.__yaaxLocalSearchIndex)) {
      this.localSearchIndex = window.__yaaxLocalSearchIndex;
      return Promise.resolve(this.localSearchIndex);
    }
    if (this.localSearchIndexPromise) return this.localSearchIndexPromise;

    const cacheKey = 'yaax_search_index_v33';
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length) {
          window.__yaaxLocalSearchIndex = parsed;
          this.localSearchIndex = parsed;
          return Promise.resolve(parsed);
        }
      }
    } catch (error) {
      // sessionStorage can be unavailable in restrictive privacy modes; continue with fetch.
    }

    this.localSearchIndexPromise = fetch('/products.json?limit=250', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then((payload) => {
        const products = Array.isArray(payload?.products) ? payload.products : [];
        const index = products.map((product) => {
          const firstVariant = Array.isArray(product.variants) ? product.variants[0] : null;
          const firstImage = Array.isArray(product.images) ? product.images[0] : null;
          const rawPrice = firstVariant?.price;
          let price = '';
          if (rawPrice !== undefined && rawPrice !== null && rawPrice !== '') {
            const numeric = Number(rawPrice);
            price = Number.isFinite(numeric)
              ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(numeric)
              : `$ ${rawPrice}`;
          }
          const src = firstImage?.src || '';
          return {
            title: product.title || '',
            url: `/products/${product.handle}`,
            handle: product.handle || '',
            image: src ? `${src}${src.includes('?') ? '&' : '?'}width=140` : '',
            price,
            available: Array.isArray(product.variants) ? product.variants.some((v) => v.available !== false) : true
          };
        });
        window.__yaaxLocalSearchIndex = index;
        this.localSearchIndex = index;
        try { sessionStorage.setItem(cacheKey, JSON.stringify(index)); } catch (error) {}
        return index;
      })
      .catch((error) => {
        console.warn('Local search index unavailable; Shopify predictive search remains active.', error);
        this.localSearchIndex = [];
        return [];
      })
      .finally(() => {
        this.localSearchIndexPromise = null;
      });

    return this.localSearchIndexPromise;
  }

  normalizeSearchText(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  damerauLevenshtein(a, b) {
    const source = this.normalizeSearchText(a).replace(/\s+/g, '');
    const target = this.normalizeSearchText(b).replace(/\s+/g, '');
    const rows = source.length + 1;
    const cols = target.length + 1;
    const matrix = Array.from({ length: rows }, () => Array(cols).fill(0));
    for (let i = 0; i < rows; i += 1) matrix[i][0] = i;
    for (let j = 0; j < cols; j += 1) matrix[0][j] = j;
    for (let i = 1; i < rows; i += 1) {
      for (let j = 1; j < cols; j += 1) {
        const cost = source[i - 1] === target[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        );
        if (
          i > 1 &&
          j > 1 &&
          source[i - 1] === target[j - 2] &&
          source[i - 2] === target[j - 1]
        ) {
          matrix[i][j] = Math.min(matrix[i][j], matrix[i - 2][j - 2] + cost);
        }
      }
    }
    return matrix[rows - 1][cols - 1];
  }

  localMatchScore(query, product) {
    const q = this.normalizeSearchText(query);
    if (q.length < 2) return 0;
    const title = this.normalizeSearchText(product.title);
    const handle = this.normalizeSearchText(product.handle || '');
    const tokens = title.split(/\s+/).filter(Boolean);

    if (title === q) return 140;
    if (title.startsWith(q)) return 130 - Math.min(title.length - q.length, 20);
    if (tokens.some((token) => token.startsWith(q))) return 120;
    if (title.includes(q)) return 100;
    if (handle.includes(q)) return 90;

    // For 2 characters we only use exact prefix/contains matching to avoid noise.
    if (q.length < 3) return 0;

    let bestDistance = Infinity;
    for (const token of tokens) {
      if (token.length < 2) continue;
      const prefixLengths = [q.length - 1, q.length, q.length + 1]
        .filter((len) => len > 0 && len <= token.length);
      for (const len of prefixLengths) {
        bestDistance = Math.min(bestDistance, this.damerauLevenshtein(q, token.slice(0, len)));
      }
    }

    if (bestDistance <= 1) return 80 - bestDistance;
    if (q.length >= 6 && bestDistance <= 2) return 60 - bestDistance;
    return 0;
  }

  getLocalMatches(searchTerm, limit = 6) {
    return (Array.isArray(this.localSearchIndex) ? this.localSearchIndex : [])
      .filter((product) => product.available !== false)
      .map((product, index) => ({ product, index, score: this.localMatchScore(searchTerm, product) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score || a.index - b.index)
      .slice(0, limit)
      .map((entry) => entry.product);
  }

  createLocalResultItem(product, idSuffix) {
    const li = document.createElement('li');
    li.id = `predictive-search-option-yaax-${idSuffix}`;
    li.className = 'yaax-local-predictive__item predictive-search__list-item';
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', 'false');

    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'yaax-local-predictive__link link link--text';
    link.tabIndex = -1;

    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = product.title || '';
      image.width = 50;
      image.height = 50;
      image.loading = 'lazy';
      image.className = 'yaax-local-predictive__image';
      link.appendChild(image);
    }

    const content = document.createElement('span');
    content.className = 'yaax-local-predictive__content';
    const title = document.createElement('span');
    title.className = 'yaax-local-predictive__title';
    title.textContent = product.title || '';
    content.appendChild(title);
    if (product.price) {
      const price = document.createElement('span');
      price.className = 'yaax-local-predictive__price';
      price.textContent = product.price;
      content.appendChild(price);
    }
    link.appendChild(content);
    li.appendChild(link);

    link.addEventListener('click', () => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'yaax_search_suggestion_click',
        search_term: this.getQuery(),
        product_handle: product.handle || ''
      });
    }, { once: true });

    return li;
  }

  buildLocalResults(searchTerm, products) {
    const wrapper = document.createElement('div');
    wrapper.className = 'yaax-local-predictive';
    wrapper.dataset.yaaxLocalSearch = searchTerm;

    const heading = document.createElement('div');
    heading.className = 'yaax-local-predictive__heading';
    heading.textContent = 'Productos sugeridos';
    wrapper.appendChild(heading);

    const list = document.createElement('ul');
    list.className = 'yaax-local-predictive__list predictive-search__results-list list-unstyled';
    list.setAttribute('role', 'group');
    products.forEach((product, index) => list.appendChild(this.createLocalResultItem(product, index + 1)));
    wrapper.appendChild(list);
    return wrapper;
  }

  renderMinimumHint() {
    this.predictiveSearchResults.innerHTML = '<div class="yaax-search-minimum-hint">Escribe una letra más para ver productos sugeridos.</div>';
    this.setAttribute('results', 'true');
    this.open();
  }

  renderLocalPreview(searchTerm) {
    const matches = this.getLocalMatches(searchTerm);
    if (!matches.length) return false;
    this.predictiveSearchResults.innerHTML = '';
    this.predictiveSearchResults.appendChild(this.buildLocalResults(searchTerm, matches));
    this.setAttribute('results', 'true');
    this.open();
    return true;
  }

  renderLocalPreviewAsync(searchTerm) {
    return this.ensureLocalSearchIndex().then(() => {
      if (this.getQuery() !== searchTerm || searchTerm.length < 2) return false;
      return this.renderLocalPreview(searchTerm);
    });
  }

  injectLocalMatches(searchTerm) {
    if (!searchTerm || searchTerm.length < 2) return;
    if (!Array.isArray(this.localSearchIndex)) {
      this.ensureLocalSearchIndex().then(() => {
        if (this.getQuery() === searchTerm) this.injectLocalMatches(searchTerm);
      });
      return;
    }
    const matches = this.getLocalMatches(searchTerm);
    if (!matches.length) return;

    const existingUrls = new Set(
      Array.from(this.predictiveSearchResults.querySelectorAll('a[href]'))
        .map((link) => {
          try { return new URL(link.href, window.location.origin).pathname; } catch (error) { return link.getAttribute('href') || ''; }
        })
    );
    const missing = matches.filter((product) => {
      try { return !existingUrls.has(new URL(product.url, window.location.origin).pathname); }
      catch (error) { return !existingUrls.has(product.url); }
    });
    if (!missing.length) return;

    const nativeList = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list');
    if (nativeList) {
      const currentItems = nativeList.querySelectorAll('li').length;
      missing.slice(0, Math.max(0, 6 - currentItems)).forEach((product, index) => {
        nativeList.appendChild(this.createLocalResultItem(product, `extra-${index + 1}`));
      });
      return;
    }

    const localBlock = this.buildLocalResults(searchTerm, missing.slice(0, 6));
    const searchForButton = this.predictiveSearchResults.querySelector('#predictive-search-option-search-keywords');
    if (searchForButton) {
      searchForButton.parentNode.insertBefore(localBlock, searchForButton);
    } else {
      this.predictiveSearchResults.appendChild(localBlock);
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, searchTerm = this.searchTerm) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.injectLocalMatches(searchTerm);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
